import { useSelector } from "react-redux";
import InputForm from "./InputFormV2";
import { State } from "../../../reducers";
import useFormaPago from "../../../actions/forma-pago.actions";
import DolarIcon from '../../../media/felsv/DollarSecondary.svg'
import { Pagination } from "../../../components/Pagination";
import { useEffect, useState } from "react";
import ModalConfirm from "../../../components/Modals/Generales/ModalConfirm";
import EditIcon from '../../../media/felsv/down-arrow-svgrepo-com.svg';
import CreditImage from '../../../media/felsv/CreditImage.svg';
import DebitImage from '../../../media/felsv/DebitImage.svg';
import DollarImage from '../../../media/felsv/DollarImage.svg';
import BankImage from '../../../media/felsv/BankImage.svg';

interface PaymentType {
    id: string;
    name: string;
    icon: string;
    width: string;
}

interface PaymentAmounts {
    [key: string]: string;
}

export default function FormaPago({ totalAPagar }: { totalAPagar?: string }) {
    const { formFormaPago, errorsFormaPago, pagos, tablePagos } = useSelector((state: State) => state.formaPago)
    const { condicionesPago, formasPago, plazosPago } = useSelector((state: State) => state.catalogos)
    const { onChangeForm, button_agregarPago, button_agregarPagoExacto, setTablePagos, actionButton_quitarpago, vaciarPagos, calculosPagos } = useFormaPago(totalAPagar || '')
    const [modalAdvertencia, setModalAdvertencia] = useState(false)
    const [condicion, setCondicion] = useState('')
    const [showContent, setShowContent] = useState(false)
    const [selectedPayments, setSelectedPayments] = useState<string[]>([]);
    const [paymentAmounts, setPaymentAmounts] = useState<PaymentAmounts>({});

    const paymentTypes: PaymentType[] = [
        { id: '01', name: 'Efectivo', icon: DollarImage, width: '12%' },
        { id: '03', name: 'Tarjeta de Crédito', icon: CreditImage, width: '10%' },
        { id: '02', name: 'Tarjeta de Débito', icon: DebitImage, width: '10%' },
        { id: '05', name: 'Transferencia Bancaria', icon: BankImage, width: '10%' }
    ];
    const primaryColor = 'rgb(19, 173, 144)';

    useEffect(() => {
        if (selectedPayments.length < 2) return;

        const total = parseFloat(totalAPagar || '0');
        const lastPaymentId = selectedPayments[selectedPayments.length - 1];

        setPaymentAmounts(prev => {
            // Sumar todos los pagos menos el último, con el estado "prev"
            const otherSum = selectedPayments
                .slice(0, -1)
                .reduce((sum, id) => sum + (parseFloat(prev[id]) || 0), 0);

            // se calcula el resto
            const remaining = total - otherSum;
            const formatted = remaining >= 0 ? remaining.toFixed(2) : prev[lastPaymentId];

            // si no se cambio nada, devolvemos anterior para no entrar en bucle
            if (prev[lastPaymentId] === formatted) {
                return prev;
            }

            return {
                ...prev,
                [lastPaymentId]: formatted
            };
        });
    }, [selectedPayments, paymentAmounts, totalAPagar]);

    useEffect(() => {
        setTablePagos(pagos)
    }, [pagos])

    const handlePaymentToggle = (paymentId: string): void => {
        const total = parseFloat(totalAPagar || '0');
        const prev = [...selectedPayments];

        if (prev.includes(paymentId)) {
            const newSelected = prev.filter(id => id !== paymentId);
            const removedAmt = parseFloat(paymentAmounts[paymentId] || '0');

            // clonamos los montos y borramos el que quitamos
            const updatedAmounts: PaymentAmounts = { ...paymentAmounts };
            delete updatedAmounts[paymentId];

            // como se decide a quién sumar el pago que se removio:
            // si se deselecciona el primero lo suma al al siguiente.
            // si se deselecciona cualquier otro, va al anterior del array.
            const idx = prev.indexOf(paymentId);
            let targetId: string | undefined;
            
            if (idx > 0) {
                targetId = prev[idx - 1];
            } else if (idx === 0 && prev.length > 1) {
                targetId = prev[1];
            }

            if (targetId) {
                const base = parseFloat(updatedAmounts[targetId] || '0');
                updatedAmounts[targetId] = (base + removedAmt).toFixed(2);
            }

            button_agregarPagoExacto(updatedAmounts);
            setSelectedPayments(newSelected);
            setPaymentAmounts(updatedAmounts);

        } else {
            if (selectedPayments.length >= 2) return;
            const newSelected = [...prev, paymentId];
            const updatedAmounts: PaymentAmounts = { ...paymentAmounts };

            if (prev.length === 0) {
                // si solo hay un metodo de pago se le coloca el total
                updatedAmounts[paymentId] = total.toFixed(2);
            } else {
                // si hay mas metodos de pago: se le coloca el restante
                const sumPrev = prev.reduce(
                    (sum, id) => sum + parseFloat(paymentAmounts[id] || '0'),
                    0
                );
                updatedAmounts[paymentId] = (total - sumPrev).toFixed(2);
            }

            button_agregarPagoExacto(updatedAmounts);
            setSelectedPayments(newSelected);
            setPaymentAmounts(updatedAmounts);
        }
    };

    const handleAmountChange = (paymentId: string, value: string) => {
        const total = parseFloat(totalAPagar || '0');
        if((parseFloat(value) > total) || parseFloat(value) < 0) return

        const updatedAmounts: Record<string, string> = {
            ...paymentAmounts,
            [paymentId]: value
        };

        // Se busca la posicion en el array de seleccionados
        const idx = selectedPayments.indexOf(paymentId);

        // Si no es el ultimo pago seleccionado, se calcula el restante para el siguiente
        if (idx > -1 && idx < selectedPayments.length - 1) {
            const nextId = selectedPayments[idx + 1];

            // Se suman todos excepto nextId
            const sumExceptNext = selectedPayments
                .filter(id => id !== nextId)
                .reduce((sum, id) => sum + parseFloat(updatedAmounts[id] || '0'), 0);

            // Se le coloca lo restante al siguiente
            updatedAmounts[nextId] = (total - sumExceptNext).toFixed(2);
        }

        button_agregarPagoExacto(updatedAmounts);
        setPaymentAmounts(updatedAmounts);
    };

    const onChangeCondicion = (e: any) => {
        if (pagos.length > 0) {
            setCondicion(e.value)
            setModalAdvertencia(true)
        } else {
            onChangeForm('condicion', e.value)
        }
    }

    const confirmChangeCondicion = () => {
        onChangeForm('condicion', condicion)
        vaciarPagos()
        setModalAdvertencia(false)
    }

    let condicionContadoCode = condicionesPago.find((condicion) => condicion.valor === 'Contado')?.codigo

    return (
        <>
            {modalAdvertencia && <ModalConfirm actionConfirm={confirmChangeCondicion} closeModalConfirm={() => setModalAdvertencia(false)} title='Cambio de condición' detail='Al realizar esta acción se eliminarán los pagos agregados previamente, ¿Deseas continuar?' />}



            <div id='w-node-_5c6af4dc-0d52-341f-2ef6-a52695e59ed4-0b1e6fa6' className='w-layout-cell'>
                <div style={{ width: '100%' }} className='card'>
                    <h3 className='card-title l'>
                        Formas de Pago
                    </h3>
                    <div className='w-layout-hflex button-actions' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px' }}>
                        {paymentTypes.map((payment: PaymentType) => {
                            const Icon = payment.icon;
                            const isSelected: boolean = selectedPayments.includes(payment.id);
                            return (
                                <div key={payment.id} >
                                    <button
                                        onClick={() => handlePaymentToggle(payment.id)}
                                        className={`m buton w-inline-block 
                                                ${isSelected
                                                ? 'm button w-inline-block'
                                                : 'm button transparent'}`} >
                                        <img src={payment.icon} style={{ width: payment.width, height: 'auto' }} /> {payment.name}
                                    </button>

                                    {isSelected && (
                                        <div className="card" style={{display:'flex', justifyContent:'center'}}>
                                            <input
                                                type="number"
                                                value={paymentAmounts[payment.id] || ''}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAmountChange(payment.id, e.target.value)}
                                                placeholder="0.00"
                                                
                                                min={0}
                                                disabled={selectedPayments.length > 1 && selectedPayments[selectedPayments.length - 1] === payment.id}
                                            />
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>

                    <hr className="separador" />

                    <h3 className='card-title 2' onClick={() => setShowContent(!showContent)} style={{ cursor: 'pointer' }}>
                        Otros (Indicar medio de pago)
                        <img src={EditIcon} style={{ marginLeft: '16px', width: '15px' }} />
                    </h3>

                    {
                        showContent &&
                        (
                            <div style={{ overflowX: "scroll" }}>
                                <InputForm required error={errorsFormaPago.condicion} disabled={parseFloat(totalAPagar || '0') === 0} onChange={onChangeCondicion} label='Condición' options={condicionesPago} keyNameText='valor' keyNameValue='codigo' value={formFormaPago.condicion} type='select' />
                                <hr className="separador" />
                                <div className='w-layout-hflex h-inputs'>
                                    <InputForm required error={errorsFormaPago.formaPago} disabled={parseFloat(totalAPagar || '0') === 0} onChange={(e: any) => onChangeForm('formaPago', e.value)} label='Forma de pago' options={formasPago} keyNameText='valor' keyNameValue='codigo' value={formFormaPago.formaPago} type='select' />
                                    <InputForm required error={errorsFormaPago.monto} disabled={parseFloat(totalAPagar || '0') === 0} leftIcon={DolarIcon} onChange={(e: any) => onChangeForm(e.target.name, e.target.value)} name='monto' label='Monto' value={formFormaPago.monto} />
                                    <InputForm maxLenght={35} error={errorsFormaPago.numeroDocumento} disabled={parseFloat(totalAPagar || '0') === 0} onChange={(e: any) => onChangeForm(e.target.name, e.target.value)} name='numeroDocumento' label='Número de documento' value={formFormaPago.numeroDocumento} />
                                </div>
                                {formFormaPago.condicion !== condicionContadoCode &&
                                    <div className='w-layout-hflex h-inputs'>
                                        <InputForm error={errorsFormaPago.plazo} disabled={parseFloat(totalAPagar || '0') === 0} onChange={(e: any) => onChangeForm('plazo', e.value)} label='Plazo' options={plazosPago} keyNameText='valor' keyNameValue='codigo' value={formFormaPago.plazo} type='select' />
                                        <InputForm maxLenght={3} error={errorsFormaPago.periodo} disabled={parseFloat(totalAPagar || '0') === 0} onChange={(e: any) => onChangeForm(e.target.name, e.target.value)} name='periodo' label='Período' value={formFormaPago.periodo} />
                                    </div>
                                }
                                <div style={{ display: 'flex', justifyContent: 'end' }}>
                                    <button className="button w-auto" onClick={button_agregarPago}>Agregar pago</button>
                                </div>
                                {pagos.length > 0 ?
                                    <>
                                        <hr className="separador" />
                                        <div style={{ overflowX: 'auto' }} className="tab-plane">
                                            <table className="table-main-responsive responsive-table-main td-left table-pointer">
                                                <thead>
                                                    <tr>
                                                        <td>Forma de pago</td>
                                                        <td>Monto</td>
                                                        <td>Número de documento</td>
                                                        <td>Plazo</td>
                                                        <td>Período</td>
                                                        <td>Acciones</td>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tablePagos.map((pago, i) => (
                                                        <tr key={i}>
                                                            <td data-label="Forma de pago">{formasPago.find((forma) => forma.codigo === pago.formaPago)?.valor}</td>
                                                            <td data-label="Monto">{parseFloat(pago.monto).toLocaleString('es-SV', { minimumFractionDigits: 2, maximumFractionDigits: 3, style: 'currency', currency: 'USD' })}</td>
                                                            <td data-label="Número de documento" dangerouslySetInnerHTML={{ __html: pago.numeroDocumento || "&nbsp;" }}></td>
                                                            <td data-label="Plazo" dangerouslySetInnerHTML={{ __html: plazosPago.find((plazo) => plazo.codigo === pago.plazo)?.valor || "&nbsp;" }}></td>
                                                            <td data-label="Período" dangerouslySetInnerHTML={{ __html: pago.periodo || "&nbsp;" }}></td>
                                                            <td data-label="Acciones">
                                                                <button
                                                                    style={{ borderRadius: '4px', color: '#fff', background: 'red' }}
                                                                    onClick={() => {
                                                                        actionButton_quitarpago(pago.id)
                                                                        //setSelectedPayments(prev => [])
                                                                        handlePaymentToggle(pago.id)
                                                                    }}
                                                                >
                                                                    Quitar
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <Pagination data={pagos} items={5} state={(items: Array<any>) => setTablePagos(items)} />
                                    </>
                                    :

                                    <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                                        <h3 className="card-title l">Sin pagos agregados</h3>
                                    </div>
                                }
                            </div>

                        )}

                    <div className="cf-calculos-producto">
                        <table>
                            <tbody>
                                <tr className="cf-total">
                                    <td>Total</td>
                                    <td>{calculosPagos().montoTotal.toLocaleString('es-SV', { minimumFractionDigits: 2, maximumFractionDigits: 2, style: 'currency', currency: 'USD' })}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>


        </>
    )
}